import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth config with token from localStorage
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  };
};

export default function TemplateSelectionPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardCredits, setCardCredits] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [cardTitle, setCardTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, templatesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/profile`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/templates`, getAuthConfig())
      ]);
      setUser(profileRes.data);
      setCardCredits(profileRes.data.card_credits || 0);
      // Filter to show templates that have card_defaults (created from cards)
      setTemplates(templatesRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
        navigate('/login');
        return;
      }
      toast.error('Gagal memuatkan data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const handleStartBlank = () => {
    setSelectedTemplate(null);
    setDialogOpen(true);
  };

  const handleCreateCard = async () => {
    if (!cardTitle.trim()) {
      toast.error('Sila masukkan tajuk kad');
      return;
    }

    setCreating(true);
    try {
      let response;
      if (selectedTemplate) {
        // Create from template
        response = await axios.post(
          `${BACKEND_URL}/api/cards/from-template/${selectedTemplate.template_id}?title=${encodeURIComponent(cardTitle.trim())}`,
          {},
          getAuthConfig()
        );
      } else {
        // Create blank card
        response = await axios.post(
          `${BACKEND_URL}/api/cards/quick`,
          { title: cardTitle.trim() },
          getAuthConfig()
        );
      }
      toast.success('Kad berjaya dicipta!');
      navigate(`/editor/${response.data.card_id}`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Tiada baki kad. Sila buat pembayaran.');
        navigate('/dashboard');
      } else {
        toast.error(error.response?.data?.detail || 'Gagal mencipta kad');
      }
    } finally {
      setCreating(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D348B] mx-auto mb-4"></div>
          <p className="text-gray-600">Memuatkan...</p>
        </div>
      </div>
    );
  }

  // Check if user has credits (or is admin)
  if (!isAdmin && cardCredits <= 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Tiada Baki Kad</h1>
          <p className="text-gray-600 mb-6">Anda perlu membeli kad terlebih dahulu sebelum mencipta kad jemputan.</p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full"
          >
            Kembali ke Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-[#3D348B] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali
          </Button>
          <h1 className="text-xl font-bold text-white">Pilih Templat</h1>
          <div className="w-20"></div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Credits Display */}
        {!isAdmin && (
          <div className="mb-6 text-center">
            <span className="text-gray-600">Baki kad: </span>
            <span className="font-bold text-[#7678ED]">{cardCredits}</span>
          </div>
        )}
        {isAdmin && (
          <div className="mb-6 text-center">
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
              Admin - Unlimited Cards
            </span>
          </div>
        )}

        {/* Template Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Start Blank Option */}
          <div
            onClick={handleStartBlank}
            className="bg-white rounded-xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-[#7678ED] cursor-pointer transition-all hover:shadow-lg group"
          >
            <div className="aspect-[3/4] flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-[#7678ED]/5 group-hover:to-[#7678ED]/10">
              <Plus className="w-16 h-16 text-gray-400 group-hover:text-emerald-600 mb-4" />
              <p className="text-gray-600 font-medium group-hover:text-[#7678ED]">Mula dari Kosong</p>
              <p className="text-sm text-gray-400 mt-1">Blank template</p>
            </div>
          </div>

          {/* Templates */}
          {templates.map((template) => (
            <div
              key={template.template_id}
              onClick={() => handleTemplateSelect(template)}
              className="bg-white rounded-xl overflow-hidden border-2 border-transparent hover:border-[#7678ED] cursor-pointer transition-all hover:shadow-lg group"
            >
              <div className="aspect-[3/4] relative overflow-hidden">
                {template.preview_image ? (
                  <img
                    src={template.preview_image}
                    alt={template.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#7678ED]/10 to-gray-100 flex items-center justify-center">
                    <Sparkles className="w-12 h-12 text-emerald-400" />
                  </div>
                )}
                {template.is_card_template && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-emerald-600 text-white text-xs rounded-full">
                    Admin Template
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                <p className="text-sm text-gray-500 truncate">{template.description}</p>
              </div>
            </div>
          ))}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Tiada templat tersedia. Pilih "Mula dari Kosong" untuk membuat kad baharu.</p>
          </div>
        )}
      </div>

      {/* Card Title Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedTemplate ? `Templat: ${selectedTemplate.name}` : 'Kad Baharu'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="card-title">Tajuk Kad</Label>
              <Input
                id="card-title"
                value={cardTitle}
                onChange={(e) => setCardTitle(e.target.value)}
                placeholder="contoh: Majlis Perkahwinan Ali & Fatimah"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateCard();
                  }
                }}
              />
              <p className="text-xs text-gray-500">Tajuk ini untuk rujukan anda sahaja</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleCreateCard}
              disabled={creating || !cardTitle.trim()}
              className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full"
            >
              {creating ? 'Mencipta...' : 'Cipta Kad'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
